<template>
  <v-card
    flat
    class="mt-5"
  >
    <div class="px-3">
      <v-card-text class="pt-5">
        <v-row>
          <v-col
            cols="12"
          >
            <v-simple-table
              height="250"
              fixed-header
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-uppercase">
                      Name
                    </th>
                    <th class="text-center text-uppercase">
                      Gender
                    </th>
                    <th class="text-center text-uppercase">
                      DOB
                    </th>
                    <th class="text-center text-uppercase">
                      Relationship
                    </th>
                    <th class="text-center text-uppercase">
                      <!-- Remove -->
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in household"
                    :key="item.hash"
                  >
                    <td>{{ item.name }}</td>
                    <td class="text-center">
                      {{ item.gender }}
                    </td>
                    <td class="text-center">
                      {{ item.dob }}
                    </td>
                    <td class="text-center">
                      {{ item.relationship }}
                    </td>
                    <td class="text-center">
                      <v-btn
                        color="error"
                        small
                        @click="removeHouseholdHandler(item.hash)"
                      >
                        <v-icon>
                          {{ icons.mdiTrashCanOutline }}
                        </v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>

        <!-- divider -->
        <v-divider></v-divider>

        <v-form
          ref="householdForm"
          class="multi-col-validation mt-10"
          lazy-validation
          @submit.prevent="addHouseholdHandler"
        >
          <v-row>
            <v-card-title class="flex-nowrap">
              <v-icon class="text--primary me-3">
                {{ icons.mdiAccountMultipleCheck }}
              </v-icon>
              <span class="text-break">Add Family Member:</span>
            </v-card-title>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="memberName"
                type="text"
                :prepend-inner-icon="icons.mdiFormTextbox"
                label="Name"
                outlined
                dense
                :rules="newClientRules.nameRules"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="2"
            >
              <v-select
                v-model="memberGender"
                dense
                outlined
                label="Gender"
                :items="genders"
                :prepend-inner-icon="icons.mdiGenderMaleFemale"
                :rules="newClientRules.anyRequired"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="2"
            >
              <v-menu
                ref="menuActivePicker"
                v-model="menuActivePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="memberDOB"
                    label="Birthday"
                    :prepend-inner-icon="icons.mdiCalendar"
                    readonly
                    v-bind="attrs"
                    dense
                    outlined
                    :rules="newClientRules.anyRequired"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="memberDOB"
                  :active-picker.sync="activePicker"
                  :max="(new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)"
                  min="1950-01-01"
                  @change="saveActivePicker"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-select
                v-model="memberRelationship"
                dense
                outlined
                label="Relationship"
                :items="relations"
                :prepend-inner-icon="icons.mdiRelationZeroOrOneToZeroOrOne"
                :rules="newClientRules.anyRequired"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              md="1"
            >
              <v-btn
                color="primary"
                type="submit"
              >
                <v-icon size="30">
                  {{ icons.mdiAccountMultiplePlus }}
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </div>

    <!-- action buttons -->
    <div class="pa-3">
      <v-card-text>
        <v-btn
          color="primary"
          class="me-3 mt-3"
          :loading="isSaving"
          @click="saveChanges"
        >
          Save Household
        </v-btn>
        <v-btn
          color="secondary"
          outlined
          class="mt-3"
          @click="resetForm"
        >
          Cancel
        </v-btn>
      </v-card-text>
    </div>
  </v-card>
</template>

<script>
import {
  mdiAccountMultiplePlus,
  mdiTrashCanOutline,
  mdiAccountMultipleCheck,
  mdiGenderMaleFemale,
  mdiCalendar,
  mdiRelationZeroOrOneToZeroOrOne,
  mdiFormTextbox,
} from '@mdi/js'

import { ref } from '@vue/composition-api'

import Swal from 'sweetalert2'
import { newClientRules } from '@/utils/form-rules'
import { capitalizeWords } from '@/utils/main'
import { axiosInst } from '@/utils/axiosIns'

export default {
  props: {
    clientId: {
      type: String,
      required: true,
    },
    householdData: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const genders = ['Male', 'Female']
    const relations = ['Parent', 'Child', 'Spouse', 'Sibling', 'Grandparent', 'Grandchild', 'Parent\'s sibling', 'Sibling\'s child', 'Aunt\'s/Uncle\'s child', 'Other']

    const household = ref([...props.householdData])
    const resetForm = () => {
      household.value = [...props.householdData]
    }

    return {
      icons: {
        mdiAccountMultiplePlus,
        mdiTrashCanOutline,
        mdiAccountMultipleCheck,
        mdiGenderMaleFemale,
        mdiCalendar,
        mdiRelationZeroOrOneToZeroOrOne,
        mdiFormTextbox,
      },
      newClientRules: newClientRules(),
      household,
      genders,
      relations,
      activePicker: null,
      menuActivePicker: false,
      memberDOB: '',
      memberGender: '',
      memberRelationship: '',
      memberName: '',
      isSaving: false,
      resetForm,

      // lastUpate: Date.now(),
    }
  },

  watch: {
    menuActivePicker(val) {
      if (val) {
        setTimeout(() => { this.activePicker = 'YEAR' })
      }
    },

    householdData(val) {
      // console.log('watch householdData', val)

      this.household = [...val]

      // this.updateList()
    },

    /*
    clientId(val) {
      console.log('watch clientId', val)

      // this.clientId = val

      // this.updateList()
    },
    */
  },

  mounted() {
    // console.log('mounted householdData', this.householdData)
  },

  methods: {
    saveActivePicker(date) {
      this.$refs.menuActivePicker.save(date)
    },

    addHouseholdHandler() {
      if (!this.$refs.householdForm.validate()) return

      this.household.push({
        hash: `${Date.now()}-${Math.floor(Math.random() * 10000)}`, // Unique hash for frontend use
        name: capitalizeWords(this.memberName),
        gender: this.memberGender,
        dob: this.memberDOB,
        relationship: this.memberRelationship,
      })

      // this.updateList()
      this.resetFormVars()
    },

    removeHouseholdHandler(hash) {
      const index = this.household.findIndex(item => item.hash === hash)
      if (index !== -1) this.household.splice(index, 1)

      // this.updateList()
    },

    /*
    updateList() {
      this.lastUpate = Date.now()
    },
    */

    resetFormVars() {
      this.memberName = ''
      this.memberGender = ''
      this.memberDOB = ''
      this.memberRelationship = ''

      this.$refs.householdForm.reset()
    },

    async saveChanges() {
      // if (!this.household.length || !this.clientId) return
      if (!this.clientId) return

      this.isSaving = true

      try {
        const rs = await axiosInst.post('client-update', {
          action: 'household',
          clientId: this.clientId,
          household: this.household,
        })

        // console.log(rs)
        if (rs.status === 200 && rs.data.error === false) {
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: 'Household has been updated',
            showConfirmButton: false,
            timer: 1500,
          })
        }
      } catch (err) {
        console.error('error:', err)
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
